import find from 'lodash/find'
import prop from 'lodash/fp/prop'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import split from 'lodash/split'
import trim from 'lodash/trim'
import moment from 'moment';
import * as Types from './types'

export type Validator = (value: any) => Types.Validation

const STRICT_URL_REGEX = /^((http|https):\/\/)[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/
const FORGIVING_URL_REGEX = /^((http|https):\/\/)[\w.-]+(?:\.[\w\.-]+)/

export const validateForgivingUrl: Validator = (url: string) => {
  return (!url || url === '') ?
      Types.missingValidation
    : (!url.match(FORGIVING_URL_REGEX)) ?
      { invalid: true, message: 'Invalid URL format', status: Types.VALIDATION_FORMAT }
    : Types.validValidation
}

export const validateOptionalForgivingUrl: Validator = (url) => {
  return (
    url &&
    url !== '' &&
    !url.match(FORGIVING_URL_REGEX)
  )
    ? { invalid: true, message: 'Invalid URL format', status: Types.VALIDATION_FORMAT }
    : Types.validValidation
}

export const validateUrl: Validator = (url) => {
  return (!url || url === '') ?
      Types.missingValidation
    : (!url.match(STRICT_URL_REGEX)) ?
      { invalid: true, message: 'Invalid URL format', status: Types.VALIDATION_FORMAT }
    : Types.validValidation
}

export const validateOptionalUrl: Validator = (url) => {
  return (
    url &&
    url !== '' &&
    !url.match(STRICT_URL_REGEX)
  )
    ? { invalid: true, message: 'Invalid URL format', status: Types.VALIDATION_FORMAT }
    : Types.validValidation
}

export const validateFutureDate: Validator = (date) => {
  const v = validateDate(date)
  if (v.invalid) { return v }
  return (moment(date, 'YYYY-MM-DD') <= moment().add(-1, 'days')) ?
      { invalid: true, message: 'Please enter future date', status: Types.VALIDATION_ERROR}
    : Types.validValidation
}

export const validateDate: Validator = (date) => {
  if (moment.isMoment(date)) {
    return Types.validValidation
  } else {
    return (!date || date === '' ) ?
        Types.missingValidation
      : ( date.length !== 10 || !(moment(date, 'YYYY-MM-DD', true).isValid()) ) ?
        { invalid: true, message: 'Invalid date (YYYY-MM-DD)', status: Types.VALIDATION_FORMAT }
      : Types.validValidation
  }
}

export const validateEvergreenDate: Validator = (date) => {
  if ( !date || date === '' ||
    (moment(date, 'YYYY-MM-DD', true).isValid()) ||
    (moment(date, 'YYYY-MM-DD h:mm:ss', true).isValid()) ||
    /^\d+\s(day|week|month)s?$/i.test(date)
  ) {
    return Types.validValidation
  } else {
    return {
      invalid: true,
      message: '"YYYY-MM-DD [hh:mm:ss]" or "X [days|weeks|months]"',
      status: Types.VALIDATION_FORMAT,
    }
  }
}

export const validateStartDate: Validator = (date) => {
  const v = validateDate(date)
  if (v.invalid) { return v }
  const sd = moment(date, 'YYYY-MM-DD')
  return (sd < moment()) ?
      { invalid: false, message: 'Start Date is earlier than today', status: Types.VALIDATION_WARN}
    : Types.validValidation
}

export const validateEndDate = (date: string, startDate: string): Types.Validation => {
    const v = validateDate(date)
    if (v.invalid) { return v }
    const ed = moment(date, 'YYYY-MM-DD')
    return (ed < moment(startDate, 'YYYY-MM-DD'))
      ?  { invalid: true, message: 'End Date must be later than Start Date', status: Types.VALIDATION_ERROR}
      : Types.validValidation
}

export const validatePresent: Validator = (s) => {
  return (!s || s === '') ?
      Types.missingValidation
    : { invalid: false, status: Types.VALIDATION_VALID }
}

export const validateNumber: Validator = (s) => {
  return (!s || s === 0) ?
      Types.missingValidation
    : { invalid: false, status: Types.VALIDATION_VALID }
}

export const validateDomain: Validator = (s) => {
  if ( s && s.includes('.') && s.length > 1) {
    return  { invalid: false, status: Types.VALIDATION_VALID }
  } else {
    return Types.missingValidation
  }
}

export const validateTitle: Validator = (title) => validatePresent(title)

export const validateTime: Validator = (time) => {
  if ( !time || time === '' ) {
    return Types.missingValidation
  }
  return /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(time) ?
      Types.validValidation
    : { invalid: true, message: 'Invalid time format (HH:MM)'}

}

const EMAIL_REGEX = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/ // tslint:disable-line

export const validateEmail: Validator = (email) => email ?
  EMAIL_REGEX.test(email) ?
    Types.validValidation :
    {invalid: true, message: 'Invalid Email format supplied.'}
  : Types.missingValidation

export const validatePassword: Validator = (password) => {
  if (password) {
    let message = ''
    if (!/[A-Z]/.test(password)) {
      message = 'Must contain uppercase letter';
    } else if (!/[a-z]/.test(password)) {
      message = 'Must contain lowercase letter';
    } else if (!/\d/.test(password)) {
      message = 'Must contain a number';
    } else if (password.length < 8 || password.length > 32) {
      message = 'Length must be between 8 to 32';
    }

    if (message === '') {
      return Types.validValidation
    } else {
      return {invalid: true, message}
    }
  } else {
    return Types.missingValidation
  }
}

export const validateConfirm = (from: string, to: string) => {
  return (from === to) || (isEmpty(to) && isEmpty(from)) ? Types.validValidation :
    isEmpty(to) ? Types.missingValidation : { invalid: true, message: 'Password does not match'}
}

const SEPARATOR_STRING = ','
const LIMIT = 10
type InputString = string
export const splitString = (inputString: InputString) => split(inputString, SEPARATOR_STRING , LIMIT)
export const trimList = (inputArray: InputString[]) => inputArray.map((item: InputString) => trim(item))

export const validateEmailList = (emailArray: InputString[]) => {
  const validateEmails = emailArray.map((item: InputString) => {
    const isValidEmail = validateEmail(item)
    return {
      invalid: isValidEmail.invalid,
    }
  })
  const findInvalid = find(validateEmails, (o: Types.Validation) => o.invalid === true)
  return isNil(findInvalid) ? {invalid: false, message: ''} : {invalid: true, message: ''}
}

type SelectInvalid = (validation: Types.Validation) => Types.Invalid
export const selectInvalid: SelectInvalid = prop('invalid')
export const validateOptionalEmail = (email: string) => email ? validateEmail(email) : Types.validValidation



// WEBPACK FOOTER //
// ./src/components/rasa-input/utils.ts